import React, { useState, useEffect, useRef } from 'react';
import ReusableForm from '../../common/ReusableForm';
import ReusableTable from '../../dashboard/ReusableTable';
import ReusableDeleteDialog from '../../common/ReusableDeleteDialog';
import { getRequest, postRequest } from '../../services/apiService'; // Removed putRequest and deleteRequest
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Button } from 'primereact/button';

const UnitManagement = ({ onBack }) => {
  const [units, setUnits] = useState([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteFormData, setDeleteFormData] = useState({ confirmation: '' });

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    status_db: 1
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  const formFields = [
    { name: 'code', label: 'Code', type: 'text', required: true },
    { name: 'name', label: 'Name', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'text' },
  ];

  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirm Deletion",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  // Fetch units function
  const fetchUnits = async () => {
    setLoading(true);
    try {
      const response = await getRequest(`master/units/?search=${encodeURIComponent(search)}&page=${currentPage}`);
      const unitsArray = response.results?.data || [];
      setUnits(unitsArray);
      setTotalRecords(response.count || 0);
    } catch (error) {
      toast.error('Failed to fetch units');
    } finally {
      setLoading(false);
    }
  };

  // Update useEffect to use fetchUnits
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      fetchUnits();
    }, 700);
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [search, currentPage]);

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  const handleSaveUnit = async () => {
    try {
      // Always use POST request for both create and update
      await postRequest('master/units/', formData);
      
      toast.success(editMode ? 'Unit updated successfully!' : 'Unit added successfully!');
      
      if (!editMode) {
        setCurrentPage(1);
      }
      
      await fetchUnits(); // Refresh table after save
      setShowFormModal(false);
    } catch (error) {
      toast.error(`Failed to ${editMode ? 'update' : 'create'} unit`);
    }
  };

  const openAddModal = () => {
    setFormData({ name: '', code: '', description: '', status_db: 1 });
    setEditMode(false);
    setShowFormModal(true);
  };

  const openEditModal = (unit) => {
    setFormData(unit);
    setEditMode(true);
    setShowFormModal(true);
  };

  const openDeleteModal = (unit) => {
    setUnitToDelete(unit);
    setDeleteFormData({ confirmation: '' });
    setShowDeleteModal(true);
  };

  const deleteUnit = async () => {
    if (deleteFormData.confirmation !== "DELETE") {
      toast.error('Please type "DELETE" to confirm');
      return;
    }

    try {
      // Use POST request with status_db: 3 for deletion
      await postRequest(`master/units/`, {
        ...unitToDelete,
        status_db: 3  // Set status to 3 for deletion
      });
      
      toast.success('Unit deleted successfully!');
      await fetchUnits(); // Refresh table after delete
      setShowDeleteModal(false);
      setUnitToDelete(null);
      setDeleteFormData({ confirmation: '' });
    } catch (error) {
      toast.error('Failed to delete unit');
    }
  };

  const onPageChange = (event) => {
    setCurrentPage(event.page + 1);
    setRowsPerPage(event.rows);
  };

  const tableColumns = [
    { field: 'name', header: 'Name' },
    { field: 'code', header: 'Code' },
    { field: 'description', header: 'Description' },
  ];

  const actionItems = [
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: (rowData) => openEditModal(rowData),
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: (rowData) => openDeleteModal(rowData),
    },
  ];

  return (
    <div className="bg-gray-100 py-8 px-4">
      <ToastContainer />
      <div className="max-w-full mx-auto">
        <div className="flex justify-between items-center mb-6">
          {/* <h2 className="text-2xl font-bold text-gray-800">Unit Management</h2> */}
          {/* <div className="flex gap-4">
            <Button
              label="Back"
              icon="pi pi-arrow-left"
              className="p-button-secondary"
              onClick={onBack}
            />
          </div> */}
        </div>

        <ReusableTable
          title="Directorate Details"
          data={units}
          setData={setUnits}
          columnFields={tableColumns}
          handleUpdate={handleSaveUnit}
          handleDelete={deleteUnit}
          setAddNewDialogVisible={openAddModal}
          actionMenu={true}
          actionItems={actionItems}
          loading={loading}
          totalRecords={totalRecords}
          rows={rowsPerPage}
          onPageChange={onPageChange}
          first={(currentPage - 1) * rowsPerPage}
          searchValue={search}
          onSearchChange={handleTableSearch}
        />

        {units.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No Deroctorate found. Add a new Deroctorate to get started.</p>
          </div>
        )}
      </div>

      {/* Add/Edit Modal with explicit wrapper */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full mx-auto max-w-2xl animate-fade-in">
            <ReusableForm
              headerText={editMode ? "Edit Directorate" : "Add Directorate"}
              formData={formData}
              setFormData={setFormData}
              fields={formFields}
              onSubmit={handleSaveUnit}
              onHide={() => setShowFormModal(false)}
              loading={loading} // Pass loading state if ReusableForm handles it
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ReusableDeleteDialog
        visible={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={deleteUnit}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        loading={loading}
        title="Delete Directorate"
        description={`Are you sure you want to delete directorate "${unitToDelete?.name}"? This action cannot be undone. Type 'DELETE' to confirm.`}
        deleteLabel="Delete"
      />
    </div>
  );
};

export default UnitManagement;